import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { CustomerProfile, FormType } from '@/models';
import { Routes } from '@/router';
import { isValidEmail } from '@/helpers';
import { CountryCode } from '@/store/ui';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import { AccountManagementEvent, AccountPageLoadEvent } from '@/services/analytics/events';
import { GuacModeLinkClickEvent } from '@/services/analytics/events/guac-mode-link-click';
import { GuacModeLinkClickType, LinkClickElementType } from '@/services/analytics/platforms/adobe-analytics/models';
import { CustomerErrorCode } from '@/services/customer/customer.service';
import { CreateAccountStart } from '@/services/analytics/platforms/adobe-analytics/constants';

interface FormError {
  lines: {
    text: string;
    class?: string[];
  }[];
}

const GENERIC_ERROR: FormError = {
  lines: [
    {
      text: 'Oops! Looks like something went wrong.',
    },
    {
      text: 'Please try again later.',
    },
  ],
};

const defaultFormState = {
  username: '',
  password: '',
  rememberMe: true,
};

type FormState = typeof defaultFormState;

@Component({
  name: 'CmgSignInForm',
})
export default class CmgSignInForm extends Vue {
  @Prop({ required: false, default: () => ({}), type: Object })
  initialFormState!: Partial<FormState>;

  @Prop({ required: false, default: true, type: Boolean })
  renderRememberMe!: boolean;

  @Prop({ required: false, default: true, type: Boolean })
  renderCountryFlag!: boolean;

  formState: FormState = { ...defaultFormState, ...this.initialFormState };

  errors: FormError[] = [];

  isLoading = false;

  showNonRewardAccountMessageUS = false;

  showNonRewardAccountMessageCA = false;

  editAccountRoute = { name: Routes.EditAccount };

  get visible(): boolean {
    return this.$appStore.state.ui.modals.signIn.visible;
  }

  get saveFavoritesText() {
    return this.$appStore.state.cms.signInContentFragment?.text2 || 'Save Favorites. Order Faster.'
  }

  get notAMemberText() {
    return this.$appStore.state.cms.signInContentFragment?.text1 || 'Not a member?';
  }
  
  get joinRewardsText() {
    return this.$appStore.state.cms.signInContentFragment?.text2 || 'Join Chipotle Rewards. Get Rewarded.';
  }
  
  get modalImageSrc(): string {
    return this.$appStore.state.cms.images.icons.pepperBadge.default;
  }

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get showErrorMessage(): boolean {
    return !!this.errors.length || this.showNonRewardAccountMessageUS || this.showNonRewardAccountMessageCA;
  }

  get fromBag(): boolean {
    return this.$appStore.state.adobe.isFromBag;
  }

  get adobeSignInSignUpExperienceA() {
    return this.$appStore.state.ui.signInSignUpVisibilityExperienceA;
  }

  get adobeSignInSignUpExperienceB() {
    return this.$appStore.state.ui.signInSignUpVisibilityExperienceB;
  }

  get customer(): CustomerProfile | null {
    const profile = this.$appStore.state.customer.profile;
    return profile && !profile.isGuest ? profile : null;
  }

  mounted() {
    this.$services.analytics.send(new AccountPageLoadEvent('sign-in'));
  }

  toggleRememberMe(): void {
    this.formState.rememberMe = !this.formState.rememberMe;
  }

  async onSubmit(): Promise<void> {
    if (this.isLoading || !this.isValid()) {
      return;
    }

    try {
      this.isLoading = true;
      this.clearErrors();
      // sign the user in
      await this.$services.customer.signIn({
        shouldTimeout: !this.formState.rememberMe,
        userName: this.formState.username,
        password: this.formState.password,
        originRoute: this.$route.name
      });

      this.proceedAfterSignIn();
      
    } catch (e) {
      // API errors are being logged from the service
      const errorCode = e.data?.errorCode;
      this.isLoading = false;
      const status = e && e.status;
      if (!status) {
        this.displayGenericError();
        return;
      }

      switch (status) {
        case 400:
          if (errorCode == CustomerErrorCode.TwoStepVerificationRequired) {
            this.$services.ui.setModalVisibility('twoStepVerificationWelcome', true, {
              email: this.formState.username, 
              phoneNumber: e.data?.phoneLastFour, 
              requiresPasswordVerification: false,
              requiresSignOutOnFailure: true,
              onSuccess: () => this.proceedAfterSignIn(),
              onFailure: () => this.$services.ui.setModalVisibility('signIn', false)
            });
          } else if (errorCode == CustomerErrorCode.AccountVerificationRequired) {
            this.$services.ui.setModalVisibility('verifyMobileNumber', true, { 
              email: this.formState.username, 
              phoneNumber: e.data?.phoneLastFour,
              requiresPasswordVerification: false,
              requiresSignOutOnFailure: true,
              onSuccess: () => this.proceedAfterSignIn(),
              onFailure: () => this.$services.ui.setModalVisibility('signIn', false)
            });
          } else if (errorCode == CustomerErrorCode.AccountLocked) {
            this.$services.ui.setModalVisibility('lockedOutModal', true);
            Vue.nextTick( () => {
              this.$services.ui.focusDiv('.cmg-locked-out-modal');
            });
          } else {
            this.errors = [
              {
                lines: [
                  { text: `The email or password you entered isn't quite right.` },
                  { text: 'As a reminder your password contains:' },
                  { text: 'at least 8 characters | ABC | abc | 123 | !@%', class: ['password-reqs'] },
                ],
              },
            ];      
          }

          break;
        default:
          this.displayGenericError();
          break;
      }

      return;
    }
  }
  
  async proceedAfterSignIn(): Promise<void> {
    this.persistCrossDomainJwt();
    this.sendAdobeTargetEvent();

    if (this.$router.currentRoute.name === Routes.GuacModeSignIn) {
      this.$services.analytics.send(new GuacModeLinkClickEvent(GuacModeLinkClickType.SignIn, LinkClickElementType.Button));
    }

    try {
      // fetch user info
      await this.$services.customer.refreshCustomerProfileInfo();
    } catch (e) {
      this.$services.logger.error('Something went wrong refreshing customer info: ', undefined, e);

      this.displayGenericError();
      this.isLoading = false;
      return;
    }

    this.isLoading = false;
    this.$emit('sign-in');
    this.postLoyaltyIdToParentFrame();
    this.sendAnalytics();
    this.setFocus();

    if(location.pathname === '/rewards') {
      this.$router.push( {name: Routes.Rewards} );
    }
    const rewardsMenuLinks = document.querySelectorAll('a[href="/rewards"]');
    rewardsMenuLinks.forEach(rewardLink => {
      rewardLink.setAttribute('href', '/order/rewards');
    });
  }

  isValid() {
    if (!this.formState.username) {
      this.errors = [{ lines: [{ text: 'Email required' }] }];
      return false;
    }

    if (!isValidEmail(this.formState.username)) {
      this.errors = [{ lines: [{ text: 'Looks like an invalid email. Try again?' }] }];
      return false;
    }

    if (!this.formState.password) {
      this.errors = [{ lines: [{ text: 'Password required' }] }];
      return false;
    }

    return true;
  }

  onClickCreateAccount() {
    if (this.$router.currentRoute.name == Routes.SignIn) {
      this.$router.push({ name: Routes.CreateAccount });
    } else if (this.$router.currentRoute.name === Routes.VendorSignIn) {
      this.$router.push({ name: Routes.VendorCreateAccount });
    } else if (this.$router.currentRoute.name === Routes.GuacModeSignIn) {
      this.$services.ui.setModalVisibility('guacModeSignIn', true, { type: FormType.CreateAccount });
      this.$services.analytics.send(new GuacModeLinkClickEvent(GuacModeLinkClickType.CreateAnAccount, LinkClickElementType.Button));
    } else {
      const route = this.$appStore.state.ui.modals.signIn.args ? this.$appStore.state.ui.modals.signIn.args.route : null;
      this.$services.ui.setModalVisibility('signIn', true, { route, type: FormType.CreateAccount });
      this.$services.ui.firstFocus();
    }

    this.$services.analytics.addLinkClickedEventToStore(CreateAccountStart);
  }

  onClickForgotPassword() {
    if (this.$router.currentRoute.name == Routes.SignIn) {
      this.$router.push({ name: Routes.PasswordResetRequestPage });
    } else if (this.$router.currentRoute.name === Routes.VendorSignIn) {
      this.$router.push({ name: Routes.VendorPasswordResetRequestPage });
    } else if (this.$router.currentRoute.name === Routes.GuacModeSignIn) {
      this.$services.ui.setModalVisibility('guacModeSignIn', false);
      this.$router.push({ name: Routes.PasswordResetRequestPage });
    } else {
      this.$services.ui.setModalVisibility('signIn', true, { type: FormType.PasswordResetRequest });
      this.$services.ui.firstFocus();
    }
    this.$services.ui.nextFocus('reset-password-title');
  }

  private displayGenericError() {
    this.errors = [GENERIC_ERROR];
  }

  /**
   * TODO: this same code is duplicated in several components, and should be refactored into service layer.
   * It sends a message to the iframe to store JWT in Azure Storage.  The intention is to keep the user signed in
   * when they switch between the US and CA sites (which are on different domains).
   */
  private persistCrossDomainJwt() {
    if (this.$router.currentRoute.name === Routes.VendorSignIn) {
      return;
    }
    const iframe = document.querySelector<HTMLIFrameElement>('#sessionFrame');
    if (iframe) {
      // Send a message to the iframe to store jwt
      const win = iframe.contentWindow;
      if (win && this.$appStore.state.customer.jwt) {
        const customerJwt = {
          jwt: this.$appStore.state.customer.jwt,
        };
        win.postMessage(JSON.stringify({ key: 'cmg-vuex', method: 'set', data: customerJwt }), this.$services.config.getSessionFrameUrl());
      }
    }
  }

  private postLoyaltyIdToParentFrame(): void {
    if (this.$router.currentRoute.name !== Routes.VendorSignIn) {
      return;
    }
    const { profile } = this.$appStore.state.customer;
    if ((profile && !this.$services.loyalty.getLoyaltyProfile(profile)) ||
      profile?.country !== CountryCode.US
    ) {
      // Older US accounts, or those created in Catering may not have a loyalty id
      this.showNonRewardAccountMessageUS = profile?.country === CountryCode.US;
      this.showNonRewardAccountMessageCA = profile?.country === CountryCode.CA;
      return;
    }
    this.$services.loyalty.postLoyaltyIdToParentFrame();
  }

  private sendAnalytics(): void {
    if (this.$router.currentRoute.name === Routes.VendorSignIn) {
      return;
    }
    const { customer } = this.$appStore.state;
    if (!customer.profile) {
      return;
    }

    this.$services.branch.loginUserEvent();

    const gaSignInLabel = this.$services.loyalty.isEnrolledInLoyalty(customer.profile) ? AnalyticsEventLabel.RewardsEnrolled : AnalyticsEventLabel.RewardsNotEnrolled;
    const optOutValue = document.cookie.split('; ').find(row => row.startsWith('opt-out'));
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.Customer,
      action: AnalyticsEventAction.SignInSuccess,
      label: gaSignInLabel,
      values: { customerEmail: optOutValue ? null : customer.profile.email },
    });
  }

  private setFocus(): void {
    const children = document.querySelectorAll('.bag-container .findChipotle-pickup');
    const focusElement = children[0] as HTMLElement | undefined;
    if (focusElement) {
      focusElement.focus();
    }
    // TODO: this seems to be overwritte on the following line?  Simply porting for now.
    this.$services.ui.nextFocus('retro-credit-landing');
    this.$services.ui.nextFocus('customer-greeting');
  }

  private clearErrors(): void {
    this.errors = [];
    this.showNonRewardAccountMessageUS = false;
    this.showNonRewardAccountMessageCA = false;
  }

  private sendAdobeTargetEvent(): void {
    if (this.$router.currentRoute.name === Routes.VendorSignIn) {
      return;
    }
    const isFromBag = this.$appStore.state.adobe.isFromBag;
    if (isFromBag) {
      this.$services.utils.targetEvent('signInFromBagSuccess');
      this.$services.utils.setIsFromBag(false);
    }
  }
}
